import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ArrowLeft, FileText, User, MapPin, Droplet, Coins, TrendingUp, Download, Cloud, Satellite, Leaf, CircleDollarSign, RefreshCw, ChevronDown, ChevronUp, AlertTriangle, Shield } from "lucide-react";
import SaakhSetuLoader from "@/components/SaakhSetuLoader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreditReport = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // External Data Integration states
  const [weatherData, setWeatherData] = useState(null);
  const [satelliteData, setSatelliteData] = useState(null);
  const [pmKisanData, setPmKisanData] = useState(null);
  const [soilHealthData, setSoilHealthData] = useState(null);
  
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [satelliteLoading, setSatelliteLoading] = useState(false);
  const [pmKisanLoading, setPmKisanLoading] = useState(false);
  const [soilHealthLoading, setSoilHealthLoading] = useState(false);
  
  const [weatherExpanded, setWeatherExpanded] = useState(false);
  const [satelliteExpanded, setSatelliteExpanded] = useState(false);
  const [pmKisanExpanded, setPmKisanExpanded] = useState(false);
  const [soilHealthExpanded, setSoilHealthExpanded] = useState(false);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null,
    existingData: null
  });
  
  // XAI (Explainable AI) state
  const [xaiData, setXaiData] = useState(null);
  const [xaiLoading, setXaiLoading] = useState(false);
  const [xaiExpanded, setXaiExpanded] = useState(false);
  const [technicalDetailsExpanded, setTechnicalDetailsExpanded] = useState(false);
  
  // Narrative XAI state (NEW)
  const [narrativeData, setNarrativeData] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);

  useEffect(() => {
    fetchReport();
    fetchNarrativeExplanation(); // Use new narrative endpoint
  }, [id]);

  const fetchNarrativeExplanation = async () => {
    setNarrativeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/farmers/${id}/narrative-explanation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('✅ Narrative data received:', response.data);
      console.log('✅ Key strengths:', response.data.key_strengths?.length);
      console.log('✅ Areas of improvement:', response.data.areas_of_improvement?.length);
      setNarrativeData(response.data);
    } catch (error) {
      console.error('❌ Error fetching narrative explanation:', error);
      // Fallback to old explanation if narrative fails
      fetchXAIExplanation();
    } finally {
      setNarrativeLoading(false);
    }
  };

  const fetchXAIExplanation = async () => {
    setXaiLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/farmers/${id}/explanation?top_n=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setXaiData(response.data);
    } catch (error) {
      console.error('Error fetching XAI explanation:', error);
      // Don't show error toast, just fail silently
    } finally {
      setXaiLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/farmers/${id}/credit-report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
      
      // Initialize external data from persisted farmer data (for page reload)
      const farmer = response.data.farmer;
      
      // Check if weather data exists (check for the full JSON or just the key parameter)
      if (farmer.weather_data) {
        try {
          const weatherDataParsed = typeof farmer.weather_data === 'string' 
            ? JSON.parse(farmer.weather_data) 
            : farmer.weather_data;
          setWeatherData(weatherDataParsed);
        } catch (e) {
          console.error('Error parsing weather data:', e);
        }
      }
      
      // Check if satellite data exists  
      if (farmer.satellite_data) {
        try {
          const satelliteDataParsed = typeof farmer.satellite_data === 'string' 
            ? JSON.parse(farmer.satellite_data) 
            : farmer.satellite_data;
          setSatelliteData(satelliteDataParsed);
        } catch (e) {
          console.error('Error parsing satellite data:', e);
        }
      }
      
      // Check if PM-KISAN data exists
      if (farmer.pm_kisan_data) {
        try {
          const pmKisanDataParsed = typeof farmer.pm_kisan_data === 'string' 
            ? JSON.parse(farmer.pm_kisan_data) 
            : farmer.pm_kisan_data;
          setPmKisanData(pmKisanDataParsed);
        } catch (e) {
          console.error('Error parsing PM-KISAN data:', e);
        }
      }
      
      // Check if soil health data exists
      if (farmer.soil_health_data) {
        try {
          const soilHealthDataParsed = typeof farmer.soil_health_data === 'string' 
            ? JSON.parse(farmer.soil_health_data) 
            : farmer.soil_health_data;
          setSoilHealthData(soilHealthDataParsed);
        } catch (e) {
          console.error('Error parsing soil health data:', e);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load credit report");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  // Handler to initiate fetch with confirmation if data already exists
  const handleFetchWeather = () => {
    if (weatherData && report?.farmer?.weather_data_fetched_at) {
      // Show confirmation dialog
      setConfirmDialog({
        open: true,
        type: 'weather',
        existingData: {
          fetchedAt: report.farmer.weather_data_fetched_at,
          fetchedBy: report.farmer.weather_data_fetched_by || 'Unknown',
          days: calculateDaysSince(report.farmer.weather_data_fetched_at)
        }
      });
    } else {
      // No existing data, fetch directly
      fetchWeatherData();
    }
  };

  // FETCH → PERSIST → DISPLAY flow for external data integrations
  const fetchWeatherData = async () => {
    if (!report?.farmer?.land_state || !report?.farmer?.land_district) {
      toast.error("Farmer land location (state/district) is required for weather data");
      return;
    }
    
    setWeatherLoading(true);
    const oldScore = report?.farmer?.calculated_credit_score;
    
    try {
      const token = localStorage.getItem('token');
      
      // STEP 1: FETCH from integration API
      const response = await axios.get(`${API}/integrations/weather`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          state: report.farmer.land_state,
          district: report.farmer.land_district
        }
      });
      
      const weatherResponse = response.data;
      setWeatherData(weatherResponse);
      setWeatherExpanded(true);
      
      // STEP 2: PERSIST to farmer profile with metadata
      const rainfallDeviation = weatherResponse.seasonal_summary?.rainfall_deviation_pct;
      const now = new Date().toISOString();
      
      if (rainfallDeviation !== undefined) {
        await axios.put(`${API}/farmers/${id}`, {
          rainfall_deviation_pct: rainfallDeviation,
          weather_data: JSON.stringify(weatherResponse),
          weather_data_fetched_at: now,
          weather_data_fetched_by: user.email
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // STEP 3: DISPLAY - Refresh report to show updated data
        await fetchReport();
        
        // Get new score and show toast with change
        const newScore = report?.farmer?.calculated_credit_score;
        if (newScore && oldScore && newScore !== oldScore) {
          const change = newScore - oldScore;
          toast.success(`Weather data refreshed! Credit score updated: ${oldScore} → ${newScore} (${change > 0 ? '+' : ''}${change} points)`, {
            duration: 5000
          });
        } else {
          toast.success("Weather data refreshed successfully!");
        }
      } else {
        toast.success("Weather data fetched");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch weather data");
    } finally {
      setWeatherLoading(false);
    }
  };

  const handleFetchSatellite = () => {
    if (satelliteData && report?.farmer?.satellite_data_fetched_at) {
      setConfirmDialog({
        open: true,
        type: 'satellite',
        existingData: {
          fetchedAt: report.farmer.satellite_data_fetched_at,
          fetchedBy: report.farmer.satellite_data_fetched_by || 'Unknown',
          days: calculateDaysSince(report.farmer.satellite_data_fetched_at)
        }
      });
    } else {
      fetchSatelliteData();
    }
  };

  const fetchSatelliteData = async () => {
    setSatelliteLoading(true);
    const oldScore = report?.farmer?.calculated_credit_score;
    
    try {
      const token = localStorage.getItem('token');
      
      // STEP 1: FETCH from integration API
      const response = await axios.get(`${API}/integrations/satellite/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const satelliteResponse = response.data;
      setSatelliteData(satelliteResponse);
      setSatelliteExpanded(true);
      
      // STEP 2: PERSIST to farmer profile with metadata
      const ndviValue = satelliteResponse.vegetation_indices?.ndvi;
      const now = new Date().toISOString();
      
      if (ndviValue !== undefined) {
        await axios.put(`${API}/farmers/${id}`, {
          ndvi_trend_score: ndviValue,
          satellite_data: JSON.stringify(satelliteResponse),
          satellite_data_fetched_at: now,
          satellite_data_fetched_by: user.email
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // STEP 3: DISPLAY - Refresh report to show updated data
        await fetchReport();
        
        const newScore = report?.farmer?.calculated_credit_score;
        if (newScore && oldScore && newScore !== oldScore) {
          const change = newScore - oldScore;
          toast.success(`Satellite data refreshed! Credit score updated: ${oldScore} → ${newScore} (${change > 0 ? '+' : ''}${change} points)`, {
            duration: 5000
          });
        } else {
          toast.success("Satellite data refreshed successfully!");
        }
      } else {
        toast.success("Satellite data fetched");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch satellite data");
    } finally {
      setSatelliteLoading(false);
    }
  };

  const handleFetchPmKisan = () => {
    if (pmKisanData && report?.farmer?.pm_kisan_data_fetched_at) {
      setConfirmDialog({
        open: true,
        type: 'pmKisan',
        existingData: {
          fetchedAt: report.farmer.pm_kisan_data_fetched_at,
          fetchedBy: report.farmer.pm_kisan_data_fetched_by || 'Unknown',
          days: calculateDaysSince(report.farmer.pm_kisan_data_fetched_at)
        }
      });
    } else {
      fetchPmKisanData();
    }
  };

  const fetchPmKisanData = async () => {
    if (!report?.farmer?.aadhaar_number) {
      toast.error("Aadhaar number is required for PM-KISAN data");
      return;
    }
    
    setPmKisanLoading(true);
    const oldScore = report?.farmer?.calculated_credit_score;
    
    try {
      const token = localStorage.getItem('token');
      
      // STEP 1: FETCH from integration API
      const response = await axios.get(`${API}/integrations/pm-kisan/${report.farmer.aadhaar_number}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const pmKisanResponse = response.data;
      setPmKisanData(pmKisanResponse);
      setPmKisanExpanded(true);
      
      // STEP 2: PERSIST to farmer profile with metadata
      const beneficiaryStatus = pmKisanResponse.beneficiary_status;
      const now = new Date().toISOString();
      
      if (beneficiaryStatus !== undefined) {
        await axios.put(`${API}/farmers/${id}`, {
          pm_kisan_beneficiary_status: beneficiaryStatus,
          pm_kisan_data: JSON.stringify(pmKisanResponse),
          pm_kisan_data_fetched_at: now,
          pm_kisan_data_fetched_by: user.email
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // STEP 3: DISPLAY - Refresh report to show updated data
        await fetchReport();
        
        const newScore = report?.farmer?.calculated_credit_score;
        if (newScore && oldScore && newScore !== oldScore) {
          const change = newScore - oldScore;
          toast.success(`PM-KISAN data refreshed! Credit score updated: ${oldScore} → ${newScore} (${change > 0 ? '+' : ''}${change} points)`, {
            duration: 5000
          });
        } else {
          toast.success("PM-KISAN data refreshed successfully!");
        }
      } else {
        toast.success("PM-KISAN data fetched");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch PM-KISAN data");
    } finally {
      setPmKisanLoading(false);
    }
  };

  const handleFetchSoilHealth = () => {
    if (soilHealthData && report?.farmer?.soil_health_data_fetched_at) {
      setConfirmDialog({
        open: true,
        type: 'soilHealth',
        existingData: {
          fetchedAt: report.farmer.soil_health_data_fetched_at,
          fetchedBy: report.farmer.soil_health_data_fetched_by || 'Unknown',
          days: calculateDaysSince(report.farmer.soil_health_data_fetched_at)
        }
      });
    } else {
      fetchSoilHealthData();
    }
  };

  const fetchSoilHealthData = async () => {
    setSoilHealthLoading(true);
    const oldScore = report?.farmer?.calculated_credit_score;
    
    try {
      const token = localStorage.getItem('token');
      
      // STEP 1: FETCH from integration API
      const response = await axios.get(`${API}/integrations/soil-health/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const soilHealthResponse = response.data;
      setSoilHealthData(soilHealthResponse);
      setSoilHealthExpanded(true);
      
      // STEP 2: PERSIST to farmer profile with metadata
      const soilHealthScore = soilHealthResponse.soil_health_score;
      const now = new Date().toISOString();
      
      if (soilHealthScore !== undefined) {
        await axios.put(`${API}/farmers/${id}`, {
          soil_health_metrics: JSON.stringify({
            soil_health_score: soilHealthScore,
            ph_level: soilHealthResponse.soil_analysis?.ph_level,
            nitrogen: soilHealthResponse.soil_analysis?.nitrogen,
            phosphorus: soilHealthResponse.soil_analysis?.phosphorus,
            potassium: soilHealthResponse.soil_analysis?.potassium,
            fetched_at: soilHealthResponse.fetched_at
          }),
          soil_health_data: JSON.stringify(soilHealthResponse),
          soil_health_data_fetched_at: now,
          soil_health_data_fetched_by: user.email
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // STEP 3: DISPLAY - Refresh report to show updated data
        await fetchReport();
        
        const newScore = report?.farmer?.calculated_credit_score;
        if (newScore && oldScore && newScore !== oldScore) {
          const change = newScore - oldScore;
          toast.success(`Soil Health data refreshed! Credit score updated: ${oldScore} → ${newScore} (${change > 0 ? '+' : ''}${change} points)`, {
            duration: 5000
          });
        } else {
          toast.success("Soil Health data refreshed successfully!");
        }
      } else {
        toast.success("Soil Health data fetched");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch soil health data");
    } finally {
      setSoilHealthLoading(false);
    }
  };

  // Handle confirmation dialog actions
  const handleConfirmRefresh = () => {
    const type = confirmDialog.type;
    setConfirmDialog({ open: false, type: null, existingData: null });
    
    // Call the appropriate fetch function
    switch(type) {
      case 'weather':
        fetchWeatherData();
        break;
      case 'satellite':
        fetchSatelliteData();
        break;
      case 'pmKisan':
        fetchPmKisanData();
        break;
      case 'soilHealth':
        fetchSoilHealthData();
        break;
      default:
        break;
    }
  };

  const handleCancelRefresh = () => {
    setConfirmDialog({ open: false, type: null, existingData: null });
  };

  const getScoreColor = (score) => {
    if (score >= 600) return "text-green-600";
    if (score >= 500) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreRating = (score) => {
    if (score >= 750) return { label: "Low Risk", color: "bg-green-100 text-green-800" };
    if (score >= 650) return { label: "Medium-Low Risk", color: "bg-blue-100 text-blue-800" };
    if (score >= 550) return { label: "Medium Risk", color: "bg-yellow-100 text-yellow-800" };
    if (score >= 450) return { label: "Medium-High Risk", color: "bg-orange-100 text-orange-800" };
    return { label: "High Risk", color: "bg-red-100 text-red-800" };
  };

  // Helper functions for external data display
  const getRainfallColor = (deviationPct) => {
    if (deviationPct === null || deviationPct === undefined) return "text-gray-600";
    const absDeviation = Math.abs(deviationPct);
    if (absDeviation < 10) return "text-green-600";
    if (absDeviation < 25) return "text-yellow-600";
    return "text-red-600";
  };

  // Helper function to calculate days since data was fetched
  const calculateDaysSince = (timestamp) => {
    if (!timestamp) return null;
    const fetchedDate = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - fetchedDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Helper function to get data age badge
  const getDataAgeBadge = (timestamp) => {
    if (!timestamp) {
      return { color: 'bg-red-100 text-red-800 border-red-200', label: 'Not Fetched', icon: '❌' };
    }
    const days = calculateDaysSince(timestamp);
    if (days === null) return { color: 'bg-red-100 text-red-800 border-red-200', label: 'Not Fetched', icon: '❌' };
    
    if (days <= 30) {
      return { color: 'bg-green-100 text-green-800 border-green-200', label: 'Fresh', icon: '🟢', days };
    } else if (days <= 90) {
      return { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Consider Refreshing', icon: '🟡', days };
    } else {
      return { color: 'bg-red-100 text-red-800 border-red-200', label: 'Refresh Recommended', icon: '🔴', days };
    }
  };

  const getNdviColor = (ndvi) => {
    if (ndvi >= 0.7) return "text-green-600";
    if (ndvi >= 0.5) return "text-yellow-600";
    return "text-red-600";
  };

  const getSoilHealthColor = (score) => {
    if (score >= 70) return "text-green-600";
    if (score >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/documents/${documentId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Document downloaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download document");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SaakhSetuLoader message="Loading credit report..." size="lg" />
      </div>
    );
  }

  if (!report) return null;

  // Use risk_category from backend if available, otherwise calculate from score
  const rating = report.risk_category 
    ? { label: report.risk_category, color: 
        report.risk_category === "Low Risk" ? "bg-green-100 text-green-800" :
        report.risk_category === "Medium-Low Risk" ? "bg-blue-100 text-blue-800" :
        report.risk_category === "Medium Risk" ? "bg-yellow-100 text-yellow-800" :
        report.risk_category === "Medium-High Risk" ? "bg-orange-100 text-orange-800" :
        "bg-red-100 text-red-800"
      }
    : getScoreRating(report.credit_score);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-button">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
            <img
              src="/saakhsetu-logo.png"
              alt="SaakhSetu"
              style={{ height: '26px' }}
              data-testid="creditreport-saakhsetu-logo"
            />
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1a202c' }}>Entitlement Report</h2>
          <p className="text-gray-600">Comprehensive entitlement evaluation for farmer</p>
        </div>

        {/* Yogyank Entitlement Score Action */}
        <Card className="mb-8 border-2" style={{ borderColor: '#667eea33' }} data-testid="yogyank-action-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Shield className="h-6 w-6" style={{ color: '#667eea' }} />
                <div>
                  <h3 className="font-semibold text-lg" style={{ color: '#1a202c' }}>Yogyank Entitlement Score</h3>
                  <p className="text-sm text-gray-500">Generate ML-powered entitlement assessment</p>
                </div>
              </div>
              <Button
                onClick={() => navigate(`/entitlement-report/${id}`)}
                data-testid="generate-entitlement-score-button"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                <Shield className="h-4 w-4 mr-2" />
                Generate Entitlement Score
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card className="lg:col-span-1" data-testid="credit-score-card">
            <CardHeader>
              <CardTitle className="text-center">Yogyank Entitlement Score</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <div className={`text-6xl font-bold mb-4 ${getScoreColor(report.credit_score)}`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {report.credit_score}
              </div>
              <Badge className={rating.color}>{rating.label}</Badge>
              <div className="mt-4 text-sm text-gray-600">
                <div>Scoring Method: {report.score_breakdown?.scoring_method || 'hybrid'}</div>
                {report.score_breakdown?.ml_score && report.score_breakdown?.rules_score && (
                  <div className="mt-2">
                    <div>ML: {Math.round(report.score_breakdown.ml_score)} (60%)</div>
                    <div>Rules: {Math.round(report.score_breakdown.rules_score)} (40%)</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center">
                <TrendingUp className="h-5 w-5 mr-2" style={{ color: '#667eea' }} />
                Credit Assessment Insights
              </CardTitle>
              <p className="text-sm text-gray-500 mt-2">
                Context-aware explanation tailored to this farmer's profile. For Yogyank ML scoring, use the "Generate Entitlement Score" button above.
              </p>
            </CardHeader>
            <CardContent>
              {narrativeLoading || xaiLoading ? (
                <div className="flex items-center justify-center py-8">
                  <SaakhSetuLoader message="Loading explanation..." size="sm" />
                </div>
              ) : narrativeData ? (
                <div className="space-y-4">
                  {/* Risk Category Summary */}
                  {narrativeData.summary && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-900">{narrativeData.summary}</p>
                    </div>
                  )}

                  {/* Key Strengths - Dynamic Narrative */}
                  <div className="border-2 border-green-200 rounded-lg overflow-hidden shadow-sm">
                    <div className="bg-gradient-to-r from-green-50 to-green-100 border-b-2 border-green-200 p-4">
                      <h4 className="font-bold text-green-900 flex items-center text-lg">
                        <span className="text-2xl mr-2">💪</span>
                        Key Strengths
                      </h4>
                      <p className="text-xs text-green-700 mt-1">Factors that helped improve the assessment</p>
                    </div>
                    <div className="p-4 space-y-3 bg-white">
                      {narrativeData.key_strengths && narrativeData.key_strengths.length > 0 ? (
                        narrativeData.key_strengths.map((strength, idx) => (
                          <div key={idx} className="p-3 bg-green-50 rounded-lg border-l-4 border-green-500">
                            <div className="flex items-start justify-between mb-2">
                              <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">{strength.severity}</span>
                              <span className="bg-green-600 text-white px-2 py-0.5 rounded text-xs font-bold">+{strength.impact_points} pts</span>
                            </div>
                            <p className="text-sm font-semibold text-gray-900 leading-relaxed mb-2">
                              {strength.narrative}
                            </p>
                            <div className="text-xs text-gray-600 flex items-center">
                              <span className="bg-gray-100 px-2 py-1 rounded">{strength.feature_name}: {strength.feature_value}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-2">No key strengths identified</p>
                      )}
                    </div>
                  </div>

                  {/* Areas of Improvement - Dynamic Narrative */}
                  <div className="border-2 border-orange-200 rounded-lg overflow-hidden shadow-sm">
                    <div className="bg-gradient-to-r from-orange-50 to-orange-100 border-b-2 border-orange-200 p-4">
                      <h4 className="font-bold text-orange-900 flex items-center text-lg">
                        <span className="text-2xl mr-2">🎯</span>
                        Areas of Improvement
                      </h4>
                      <p className="text-xs text-orange-700 mt-1">Opportunities to reach a better risk category</p>
                    </div>
                    <div className="p-4 space-y-3 bg-white">
                      {narrativeData.areas_of_improvement && narrativeData.areas_of_improvement.length > 0 ? (
                        narrativeData.areas_of_improvement.map((area, idx) => (
                          <div key={idx} className="p-3 bg-red-50 rounded-lg border-l-4 border-red-500">
                            <div className="flex items-start justify-between mb-2">
                              <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">{area.severity}</span>
                              <span className="bg-red-600 text-white px-2 py-0.5 rounded text-xs font-bold">{area.impact_points} pts</span>
                            </div>
                            <p className="text-sm font-semibold text-gray-900 leading-relaxed">
                              {area.narrative}
                            </p>
                            <div className="text-xs text-gray-600 mt-2 flex items-center">
                              <span className="bg-gray-100 px-2 py-1 rounded">{area.feature_name}: {area.feature_value}</span>
                            </div>
                            {area.recommendation && (
                              <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                                <p className="text-xs text-blue-900">
                                  <span className="font-semibold">💡 Recommendation:</span> {area.recommendation}
                                </p>
                              </div>
                            )}
                            {area.prescriptive_action && area.prescriptive_action.actions && (
                              <div className="mt-2 p-3 bg-green-50 rounded border-2 border-green-300">
                                <p className="text-xs font-bold text-green-900 mb-2">
                                  🎯 To reach '{area.prescriptive_action.target_risk_category}':
                                </p>
                                {area.prescriptive_action.actions.map((action, actionIdx) => (
                                  <div key={actionIdx} className="mb-2 last:mb-0">
                                    <p className="text-xs text-green-900">
                                      <span className="font-semibold">[Option {actionIdx + 1}]</span> {action.description}
                                    </p>
                                    <p className="text-xs text-green-700 italic ml-4 mt-0.5">→ {action.impact}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-2">No areas of improvement identified</p>
                      )}
                    </div>
                  </div>

                  {/* Improvement Path - Consolidated Prescriptive Section */}
                  {narrativeData.improvement_path && narrativeData.improvement_path.applicable && (
                    <div className="border-2 border-blue-200 rounded-lg overflow-hidden shadow-sm mt-4">
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-b-2 border-blue-200 p-4">
                        <h4 className="font-bold text-blue-900 flex items-center text-lg">
                          <span className="text-2xl mr-2">🚀</span>
                          Your Path to Improvement
                        </h4>
                        <p className="text-xs text-blue-700 mt-1">Actionable steps to reach the next better risk category</p>
                      </div>
                      <div className="p-4 bg-white">
                        <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 rounded">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{narrativeData.improvement_path.message}</p>
                            <p className="text-xs text-gray-600 mt-1">
                              Current: <span className="font-semibold text-orange-700">{narrativeData.improvement_path.current_risk}</span> (Score: {narrativeData.improvement_path.current_score}) 
                              → Target: <span className="font-semibold text-green-700">{narrativeData.improvement_path.target_risk}</span>
                            </p>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          {narrativeData.improvement_path.priority_actions?.map((action, idx) => (
                            <div key={idx} className="p-3 bg-gray-50 rounded-lg border-l-4 border-blue-500">
                              <div className="flex items-start justify-between mb-2">
                                <h5 className="text-sm font-bold text-gray-900">Priority {idx + 1}: {action.feature_name}</h5>
                                <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs font-bold">-{action.impact_points} pts</span>
                              </div>
                              <p className="text-xs text-gray-700 mb-2">
                                Current: <span className="font-semibold">{action.current_value}</span> → Target: <span className="font-semibold text-green-700">{action.target_value}</span>
                              </p>
                              <div className="space-y-2">
                                {action.actions?.map((a, aIdx) => (
                                  <div key={aIdx} className="bg-white p-2 rounded border border-blue-200">
                                    <p className="text-xs text-gray-900">
                                      <span className="font-semibold text-blue-700">[Action {aIdx + 1}]</span> {a.description}
                                    </p>
                                    <p className="text-xs text-gray-600 italic ml-4 mt-1">✓ {a.impact}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : xaiData ? (
                <div className="space-y-4">
                  {/* Positive Factors - Strengths */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-green-50 border-b border-green-100 p-3">
                      <h4 className="font-semibold text-green-900 flex items-center">
                        <span className="text-lg mr-2">✅</span>
                        Strengths (Helping the Score)
                      </h4>
                    </div>
                    <div className="p-3 space-y-2">
                      {xaiData.ml_explanations
                        ?.filter(exp => exp.impact === 'positive')
                        .slice(0, 5)
                        .map((exp, idx) => (
                          <div key={idx} className="flex items-start justify-between p-2 hover:bg-gray-50 rounded">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">{exp.description}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {exp.display_name || exp.feature_name}: {exp.feature_value}
                              </div>
                            </div>
                            <div className="ml-3 text-right">
                              <Badge className="bg-green-100 text-green-800">
                                +{exp.percentage_contribution?.toFixed(1)}%
                              </Badge>
                            </div>
                          </div>
                        ))}
                      {xaiData.ml_explanations?.filter(exp => exp.impact === 'positive').length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-2">No positive factors identified</p>
                      )}
                    </div>
                  </div>

                  {/* Negative Factors - Areas to Improve */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-orange-50 border-b border-orange-100 p-3">
                      <h4 className="font-semibold text-orange-900 flex items-center">
                        <span className="text-lg mr-2">⚠️</span>
                        Areas for Improvement
                      </h4>
                    </div>
                    <div className="p-3 space-y-2">
                      {xaiData.ml_explanations
                        ?.filter(exp => exp.impact === 'negative')
                        .slice(0, 5)
                        .map((exp, idx) => (
                          <div key={idx} className="flex items-start justify-between p-2 hover:bg-gray-50 rounded">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">{exp.description}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {exp.display_name || exp.feature_name}: {exp.feature_value}
                              </div>
                            </div>
                            <div className="ml-3 text-right">
                              <Badge className="bg-orange-100 text-orange-800">
                                {exp.percentage_contribution?.toFixed(1)}%
                              </Badge>
                            </div>
                          </div>
                        ))}
                      {xaiData.ml_explanations?.filter(exp => exp.impact === 'negative').length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-2">No negative factors identified</p>
                      )}
                    </div>
                  </div>

                  {/* View Detailed Breakdown - Collapsible */}
                  <div className="border rounded-lg">
                    <button
                      onClick={() => setXaiExpanded(!xaiExpanded)}
                      className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-700">View Detailed Breakdown</span>
                      {xaiExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {xaiExpanded && (
                      <div className="border-t p-4 space-y-4 bg-gray-50">
                        {/* ML Explanations */}
                        <div>
                          <h5 className="font-semibold text-sm text-gray-700 mb-3">ML Model Insights (60% weight)</h5>
                          <div className="space-y-2">
                            {xaiData.ml_explanations?.slice(0, 10).map((exp, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm p-2 bg-white rounded border">
                                <div className="flex-1">
                                  <span className="text-gray-700 font-medium">{exp.display_name || exp.feature_name}:</span>
                                  <span className="text-gray-900 ml-2">{exp.feature_value}</span>
                                </div>
                                <div className="flex items-center space-x-2 ml-3">
                                  <Badge className={exp.impact === 'positive' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                    {exp.impact === 'positive' ? '+' : ''}{exp.percentage_contribution?.toFixed(1)}%
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Rules Explanations */}
                        {xaiData.rules_explanations && xaiData.rules_explanations.length > 0 && (
                          <div>
                            <h5 className="font-semibold text-sm text-gray-700 mb-3">Rules-Based Assessment (40% weight)</h5>
                            <div className="space-y-2">
                              {xaiData.rules_explanations.map((rule, idx) => (
                                <div key={idx} className="p-3 bg-white rounded border">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="font-medium text-gray-900">{rule.category}</span>
                                    <Badge className={rule.impact === 'positive' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                                      {rule.score?.toFixed(1)}/100
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-gray-600">{rule.description}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Technical Details - Collapsible */}
                  <div className="border rounded-lg">
                    <button
                      onClick={() => setTechnicalDetailsExpanded(!technicalDetailsExpanded)}
                      className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-700">Technical Details</span>
                      {technicalDetailsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {technicalDetailsExpanded && (
                      <div className="border-t p-4 bg-gray-50">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">Scoring Method:</span>
                            <Badge className="bg-purple-100 text-purple-800">{xaiData.scoring_method || 'hybrid'}</Badge>
                          </div>
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">ML Score (60% weight):</span>
                            <span className="font-semibold text-purple-600">{report.score_breakdown?.ml_score ? Math.round(report.score_breakdown.ml_score) : 'N/A'}</span>
                          </div>
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">Rules Score (40% weight):</span>
                            <span className="font-semibold text-blue-600">{report.score_breakdown?.rules_score ? Math.round(report.score_breakdown.rules_score) : 'N/A'}</span>
                          </div>
                          <div className="border-t pt-2 flex justify-between items-center">
                            <span className="font-bold text-gray-700">Final Hybrid Score:</span>
                            <span className={`text-xl font-bold ${getScoreColor(xaiData.hybrid_score || report.calculated_credit_score)}`}>
                              {xaiData.hybrid_score || report.calculated_credit_score}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-2">
                            <p>ML Contribution: {xaiData.ml_contribution || 'N/A'}</p>
                            <p>Rules Contribution: {xaiData.rules_contribution || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">XAI explanation not available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="mb-8" data-testid="farmer-details-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="h-5 w-5 mr-2" style={{ color: '#667eea' }} />
              Farmer Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Personal Information</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium">{report.farmer.farmer_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Contact:</span>
                    <span className="font-medium">{report.farmer.contact_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Aadhaar:</span>
                    <span className="font-medium">{report.farmer.aadhaar_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Experience:</span>
                    <span className="font-medium">{report.farmer.farming_experience_years} years</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Farm Information</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Land Size:</span>
                    <span className="font-medium">{report.farmer.total_land_holding_size_acres} acres</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ownership:</span>
                    <span className="font-medium">{report.farmer.farm_land_ownership_status}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Irrigation:</span>
                    <span className="font-medium">{report.farmer.irrigation_status}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Loan Type:</span>
                    <span className="font-medium">{report.farmer.existing_loan_type || 'None'}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* External Data Integrations - Only for Credit Officers and System Admins */}
        {(user.role === "Credit Officer" || user.role === "System Administrator") && (
          <Card className="mb-8" data-testid="external-data-card">
            <CardHeader>
              <CardTitle className="flex items-center">
                <RefreshCw className="h-5 w-5 mr-2" style={{ color: '#667eea' }} />
                External Data Integrations
              </CardTitle>
              <p className="text-sm text-gray-500 mt-2">
                Fetch real-time data from government and satellite sources. Data is automatically saved to the farmer profile.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Weather Data Card */}
                <Card className="border-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Cloud className="h-5 w-5 mr-2 text-blue-500" />
                        <CardTitle className="text-lg">Weather Data</CardTitle>
                      </div>
                      <Button
                        size="sm"
                        onClick={handleFetchWeather}
                        disabled={weatherLoading}
                        data-testid="fetch-weather-button"
                      >
                        {weatherLoading ? (
                          <span className="flex items-center gap-1.5">
                            <img src="/saakhsetu-loader.webp" alt="" style={{ width: 16, height: 16 }} />
                            Fetching...
                          </span>
                        ) : (weatherData ? "Refresh" : "Fetch")}
                      </Button>
                    </div>
                    {/* Data Age Badge */}
                    {report?.farmer?.weather_data_fetched_at && (
                      <div className="mt-2">
                        <Badge className={`${getDataAgeBadge(report.farmer.weather_data_fetched_at).color} border`}>
                          {getDataAgeBadge(report.farmer.weather_data_fetched_at).icon} {getDataAgeBadge(report.farmer.weather_data_fetched_at).label}
                          {getDataAgeBadge(report.farmer.weather_data_fetched_at).days !== undefined && 
                            ` (${getDataAgeBadge(report.farmer.weather_data_fetched_at).days} days ago)`}
                        </Badge>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    {weatherData ? (
                      <div>
                        {/* Fetched By Info */}
                        {report?.farmer?.weather_data_fetched_by && (
                          <div className="mb-3 text-xs text-gray-600">
                            Fetched by: <span className="font-medium">{report.farmer.weather_data_fetched_by}</span> ({user.role})
                          </div>
                        )}
                        
                        {/* Warning for old data */}
                        {report?.farmer?.weather_data_fetched_at && calculateDaysSince(report.farmer.weather_data_fetched_at) > 90 && (
                          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded flex items-start space-x-2">
                            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-red-800">
                              ⚠️ Data is {calculateDaysSince(report.farmer.weather_data_fetched_at)} days old. Refresh strongly recommended for accurate assessment.
                            </p>
                          </div>
                        )}
                        
                        <button
                          onClick={() => setWeatherExpanded(!weatherExpanded)}
                          className="flex items-center justify-between w-full text-left"
                        >
                          <span className="text-sm font-medium text-gray-700">
                            Last fetched: {formatDate(report.farmer.weather_data_fetched_at || weatherData.fetched_at)}
                          </span>
                          {weatherExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                        
                        {weatherExpanded && (
                          <div className="mt-4 space-y-3">
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <div className="text-xs text-gray-600 mb-1">CRITICAL PARAMETER</div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium">Rainfall Deviation</span>
                                <span className={`text-lg font-bold ${getRainfallColor(weatherData.seasonal_summary?.rainfall_deviation_pct)}`}>
                                  {weatherData.seasonal_summary?.rainfall_deviation_pct?.toFixed(1)}%
                                </span>
                              </div>
                            </div>
                            
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Temperature:</span>
                                <span className="font-medium">{weatherData.current?.temperature}°C</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Humidity:</span>
                                <span className="font-medium">{weatherData.current?.humidity}%</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Season:</span>
                                <span className="font-medium">{weatherData.seasonal_summary?.season}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Total Rainfall:</span>
                                <span className="font-medium">{weatherData.seasonal_summary?.total_rainfall_mm} mm</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No data fetched yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Satellite Data Card */}
                <Card className="border-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Satellite className="h-5 w-5 mr-2 text-purple-500" />
                        <CardTitle className="text-lg">Satellite Data</CardTitle>
                      </div>
                      <Button
                        size="sm"
                        onClick={handleFetchSatellite}
                        disabled={satelliteLoading}
                        data-testid="fetch-satellite-button"
                      >
                        {satelliteLoading ? (
                          <span className="flex items-center gap-1.5">
                            <img src="/saakhsetu-loader.webp" alt="" style={{ width: 16, height: 16 }} />
                            Fetching...
                          </span>
                        ) : (satelliteData ? "Refresh" : "Fetch")}
                      </Button>
                    </div>
                    {report?.farmer?.satellite_data_fetched_at && (
                      <div className="mt-2">
                        <Badge className={`${getDataAgeBadge(report.farmer.satellite_data_fetched_at).color} border`}>
                          {getDataAgeBadge(report.farmer.satellite_data_fetched_at).icon} {getDataAgeBadge(report.farmer.satellite_data_fetched_at).label}
                          {getDataAgeBadge(report.farmer.satellite_data_fetched_at).days !== undefined && 
                            ` (${getDataAgeBadge(report.farmer.satellite_data_fetched_at).days} days ago)`}
                        </Badge>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    {satelliteData ? (
                      <div>
                        {report?.farmer?.satellite_data_fetched_by && (
                          <div className="mb-3 text-xs text-gray-600">
                            Fetched by: <span className="font-medium">{report.farmer.satellite_data_fetched_by}</span> ({user.role})
                          </div>
                        )}
                        {report?.farmer?.satellite_data_fetched_at && calculateDaysSince(report.farmer.satellite_data_fetched_at) > 90 && (
                          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded flex items-start space-x-2">
                            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-red-800">
                              ⚠️ Data is {calculateDaysSince(report.farmer.satellite_data_fetched_at)} days old. Refresh strongly recommended for accurate assessment.
                            </p>
                          </div>
                        )}
                        <button
                          onClick={() => setSatelliteExpanded(!satelliteExpanded)}
                          className="flex items-center justify-between w-full text-left"
                        >
                          <span className="text-sm font-medium text-gray-700">
                            Last fetched: {formatDate(report.farmer.satellite_data_fetched_at || satelliteData.fetched_at)}
                          </span>
                          {satelliteExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                        
                        {satelliteExpanded && (
                          <div className="mt-4 space-y-3">
                            <div className="bg-purple-50 p-3 rounded-lg">
                              <div className="text-xs text-gray-600 mb-1">CRITICAL PARAMETER</div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium">NDVI Score</span>
                                <span className={`text-lg font-bold ${getNdviColor(satelliteData.vegetation_indices?.ndvi)}`}>
                                  {satelliteData.vegetation_indices?.ndvi?.toFixed(2)}
                                </span>
                              </div>
                            </div>
                            
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Interpretation:</span>
                                <span className="font-medium">{satelliteData.vegetation_indices?.ndvi_interpretation}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Crop Health:</span>
                                <Badge className={
                                  satelliteData.crop_health?.status === "Good" ? "bg-green-100 text-green-800" :
                                  satelliteData.crop_health?.status === "Fair" ? "bg-yellow-100 text-yellow-800" :
                                  "bg-red-100 text-red-800"
                                }>
                                  {satelliteData.crop_health?.status}
                                </Badge>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Stress Level:</span>
                                <span className="font-medium">{satelliteData.crop_health?.stress_level}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">6-Month Trend:</span>
                                <span className="font-medium">{satelliteData.historical_trend?.length || 0} data points</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No data fetched yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* PM-KISAN Data Card */}
                <Card className="border-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CircleDollarSign className="h-5 w-5 mr-2 text-green-500" />
                        <CardTitle className="text-lg">PM-KISAN</CardTitle>
                      </div>
                      <Button
                        size="sm"
                        onClick={handleFetchPmKisan}
                        disabled={pmKisanLoading}
                        data-testid="fetch-pmkisan-button"
                      >
                        {pmKisanLoading ? (
                          <span className="flex items-center gap-1.5">
                            <img src="/saakhsetu-loader.webp" alt="" style={{ width: 16, height: 16 }} />
                            Fetching...
                          </span>
                        ) : (pmKisanData ? "Refresh" : "Fetch")}
                      </Button>
                    </div>
                    {report?.farmer?.pm_kisan_data_fetched_at && (
                      <div className="mt-2">
                        <Badge className={`${getDataAgeBadge(report.farmer.pm_kisan_data_fetched_at).color} border`}>
                          {getDataAgeBadge(report.farmer.pm_kisan_data_fetched_at).icon} {getDataAgeBadge(report.farmer.pm_kisan_data_fetched_at).label}
                          {getDataAgeBadge(report.farmer.pm_kisan_data_fetched_at).days !== undefined && 
                            ` (${getDataAgeBadge(report.farmer.pm_kisan_data_fetched_at).days} days ago)`}
                        </Badge>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    {pmKisanData ? (
                      <div>
                        {report?.farmer?.pm_kisan_data_fetched_by && (
                          <div className="mb-3 text-xs text-gray-600">
                            Fetched by: <span className="font-medium">{report.farmer.pm_kisan_data_fetched_by}</span> ({user.role})
                          </div>
                        )}
                        {report?.farmer?.pm_kisan_data_fetched_at && calculateDaysSince(report.farmer.pm_kisan_data_fetched_at) > 90 && (
                          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded flex items-start space-x-2">
                            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-red-800">
                              ⚠️ Data is {calculateDaysSince(report.farmer.pm_kisan_data_fetched_at)} days old. Refresh strongly recommended for accurate assessment.
                            </p>
                          </div>
                        )}
                        <button
                          onClick={() => setPmKisanExpanded(!pmKisanExpanded)}
                          className="flex items-center justify-between w-full text-left"
                        >
                          <span className="text-sm font-medium text-gray-700">
                            Last fetched: {formatDate(report.farmer.pm_kisan_data_fetched_at || pmKisanData.fetched_at)}
                          </span>
                          {pmKisanExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                        
                        {pmKisanExpanded && (
                          <div className="mt-4 space-y-3">
                            <div className={`p-3 rounded-lg ${pmKisanData.beneficiary_status ? 'bg-green-50' : 'bg-gray-50'}`}>
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium">Beneficiary Status</span>
                                <Badge className={pmKisanData.beneficiary_status ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                                  {pmKisanData.beneficiary_status ? "Enrolled" : "Not Enrolled"}
                                </Badge>
                              </div>
                            </div>
                            
                            {pmKisanData.beneficiary_status && (
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Total Received:</span>
                                  <span className="font-medium text-green-600">₹{pmKisanData.total_received?.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Registration Date:</span>
                                  <span className="font-medium">{formatDate(pmKisanData.registration_date)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Last Payment:</span>
                                  <span className="font-medium">{formatDate(pmKisanData.last_payment_date)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Payment History:</span>
                                  <span className="font-medium">{pmKisanData.payment_history?.length || 0} installments</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No data fetched yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Soil Health Card */}
                <Card className="border-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Leaf className="h-5 w-5 mr-2 text-amber-500" />
                        <CardTitle className="text-lg">Soil Health</CardTitle>
                      </div>
                      <Button
                        size="sm"
                        onClick={handleFetchSoilHealth}
                        disabled={soilHealthLoading}
                        data-testid="fetch-soil-health-button"
                      >
                        {soilHealthLoading ? (
                          <span className="flex items-center gap-1.5">
                            <img src="/saakhsetu-loader.webp" alt="" style={{ width: 16, height: 16 }} />
                            Fetching...
                          </span>
                        ) : (soilHealthData ? "Refresh" : "Fetch")}
                      </Button>
                    </div>
                    {report?.farmer?.soil_health_data_fetched_at && (
                      <div className="mt-2">
                        <Badge className={`${getDataAgeBadge(report.farmer.soil_health_data_fetched_at).color} border`}>
                          {getDataAgeBadge(report.farmer.soil_health_data_fetched_at).icon} {getDataAgeBadge(report.farmer.soil_health_data_fetched_at).label}
                          {getDataAgeBadge(report.farmer.soil_health_data_fetched_at).days !== undefined && 
                            ` (${getDataAgeBadge(report.farmer.soil_health_data_fetched_at).days} days ago)`}
                        </Badge>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    {soilHealthData ? (
                      <div>
                        {report?.farmer?.soil_health_data_fetched_by && (
                          <div className="mb-3 text-xs text-gray-600">
                            Fetched by: <span className="font-medium">{report.farmer.soil_health_data_fetched_by}</span> ({user.role})
                          </div>
                        )}
                        {report?.farmer?.soil_health_data_fetched_at && calculateDaysSince(report.farmer.soil_health_data_fetched_at) > 90 && (
                          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded flex items-start space-x-2">
                            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-red-800">
                              ⚠️ Data is {calculateDaysSince(report.farmer.soil_health_data_fetched_at)} days old. Refresh strongly recommended for accurate assessment.
                            </p>
                          </div>
                        )}
                        <button
                          onClick={() => setSoilHealthExpanded(!soilHealthExpanded)}
                          className="flex items-center justify-between w-full text-left"
                        >
                          <span className="text-sm font-medium text-gray-700">
                            Last fetched: {formatDate(report.farmer.soil_health_data_fetched_at || soilHealthData.fetched_at)}
                          </span>
                          {soilHealthExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                        
                        {soilHealthExpanded && (
                          <div className="mt-4 space-y-3">
                            <div className="bg-amber-50 p-3 rounded-lg">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium">Soil Health Score</span>
                                <span className={`text-lg font-bold ${getSoilHealthColor(soilHealthData.soil_health_score)}`}>
                                  {soilHealthData.soil_health_score?.toFixed(1)}/100
                                </span>
                              </div>
                            </div>
                            
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Nitrogen (N):</span>
                                <Badge>{soilHealthData.soil_analysis?.nitrogen}</Badge>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Phosphorus (P):</span>
                                <Badge>{soilHealthData.soil_analysis?.phosphorus}</Badge>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Potassium (K):</span>
                                <Badge>{soilHealthData.soil_analysis?.potassium}</Badge>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">pH Level:</span>
                                <span className="font-medium">{soilHealthData.soil_analysis?.ph_level}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Recommendations:</span>
                                <span className="font-medium">{soilHealthData.recommendations?.length || 0}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No data fetched yet</p>
                    )}
                  </CardContent>
                </Card>

              </div>
            </CardContent>
          </Card>
        )}

        <Card data-testid="documents-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2" style={{ color: '#667eea' }} />
              Uploaded Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            {report.documents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {report.documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                    data-testid={`document-${doc.document_type}`}
                  >
                    <div className="flex items-center space-x-3">
                      <FileText className="h-8 w-8 text-gray-400" />
                      <div>
                        <div className="font-medium">{doc.document_type}</div>
                        <div className="text-sm text-gray-500">{doc.filename}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className="bg-green-100 text-green-800">Verified</Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                        data-testid={`download-document-${doc.document_type}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No documents uploaded yet</p>
              </div>
            )}}
          </CardContent>
        </Card>
      </main>

      {/* Confirmation Dialog for Refresh */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && handleCancelRefresh()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Refresh External Data?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {confirmDialog.existingData && (
                <>
                  <p>
                    Current data was fetched <strong>{confirmDialog.existingData.days} days ago</strong> by{' '}
                    <strong>{confirmDialog.existingData.fetchedBy}</strong>.
                  </p>
                  <p>
                    Fetching new data will replace existing values and may change the credit score.
                  </p>
                  <p className="text-sm text-gray-600">
                    This action will be logged in the audit trail.
                  </p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelRefresh}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRefresh}>
              Refresh Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CreditReport;